/**
 * Local Bridge: manage companies in the local database (list, add).
 * Uses backend Local Bridge API (PostgreSQL).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, Form, Input, Button, Table, message, Spin, Alert, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, EditOutlined, DollarOutlined } from '@ant-design/icons';
import { isLocalBridgeApiEnabled } from '../services/bridgeSessionDataAdapter';
import { listCompanies, createCompany, updateCompany, type LocalCompany } from '../services/localBridgeSessionApi';

export const LocalBridgeCompaniesPage: React.FC = () => {
  const [companies, setCompanies] = useState<LocalCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<{ name: string }>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<LocalCompany | null>(null);
  const [editForm] = Form.useForm<{ name: string }>();
  const [submittingEdit, setSubmittingEdit] = useState(false);

  const enabled = isLocalBridgeApiEnabled();

  const loadCompanies = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const list = await listCompanies();
      setCompanies(list);
    } catch {
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const company = await createCompany(values.name);
      if (company) {
        message.success('Company created');
        form.resetFields();
        loadCompanies();
      } else {
        message.error('Failed to create company');
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      message.error('Please enter company name');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (company: LocalCompany) => {
    setEditingCompany(company);
    editForm.setFieldsValue({ name: company.name });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingCompany(null);
    editForm.resetFields();
  };

  const handleEditSubmit = async () => {
    if (!editingCompany) return;
    try {
      const values = await editForm.validateFields();
      setSubmittingEdit(true);
      const updated = await updateCompany(editingCompany.id, { name: values.name });
      if (updated) {
        message.success('Company updated');
        closeEditModal();
        loadCompanies();
      } else {
        message.error('Failed to update company');
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      message.error('Please enter company name');
    } finally {
      setSubmittingEdit(false);
    }
  };

  if (!enabled) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="info"
          message="Local Bridge API not configured"
          description="Local Bridge API is not available. Ensure the backend is running."
        />
      </div>
    );
  }

  const columns: ColumnsType<LocalCompany> = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => id.slice(0, 8) + '…',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <>
          <Link to={`/local-bridge/companies/${record.id}/transactions`}>
            <Button type="link" size="small" icon={<DollarOutlined />}>
              Transactions
            </Button>
          </Link>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
            Edit
          </Button>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Link to="/local-bridge">
          <Button icon={<ArrowLeftOutlined />}>Local Bridge</Button>
        </Link>
      </div>
      <h1 style={{ marginBottom: 24 }}>Companies</h1>
      <p style={{ color: '#6b7280', marginBottom: 24 }}>
        Companies in the local bridge database. Create companies here, then use them when creating bridge sessions.
      </p>

      <Card title="Add company" style={{ marginBottom: 24 }}>
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="Company name" rules={[{ required: true }]}>
            <Input placeholder="Company name" style={{ maxWidth: 320 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Create company
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="Companies">
        {loading ? (
          <Spin size="large" />
        ) : companies.length === 0 ? (
          <p style={{ color: '#6b7280' }}>No companies yet. Add a company above.</p>
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={companies}
            pagination={false}
            size="middle"
          />
        )}
      </Card>

      <Modal
        title="Edit company"
        open={editModalOpen}
        onCancel={closeEditModal}
        onOk={handleEditSubmit}
        confirmLoading={submittingEdit}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Company name" rules={[{ required: true }]}>
            <Input placeholder="Company name" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LocalBridgeCompaniesPage;
